import { useState, useEffect, useCallback } from "react";
import Select, {
  MultiValue,
  InputActionMeta,
  components,
  OptionProps,
  MultiValueProps,
  MultiValueRemoveProps,
  MenuProps,
} from "react-select";
import { PatientRelationAxiosInstance } from "../../Network/PatientRelationAxiosInstance";
import { Option } from "../../interface/Option";

interface PatientOption extends Option {
  patientName?: string;
  nationalId?: string;
  memberId?: string;
  patientPassport?: string;
  dbId?: number;
  is_active?: boolean;
  status?: string;
  __inactive?: boolean;
}

interface VipPatientSelectorProps {
  onChange: (selected: Option[]) => void;
  clear?: boolean;
  defaultValue?: Option[];
  value?: Option[];
  qaIdPrefix?: string;
}

// Only a confirmed inactive status locks an existing reference.
const isInactivePatient = (item: any): boolean => {
  if (!item || typeof item !== "object") return false;
  const value = item.__inactive === true ? false :
    item.is_active ?? item.Is_active ?? item.active ?? item.isActive ??
    item.status ?? item.Status;
  return value === false || value === 0 || value === "0" ||
    (typeof value === "string" && ["inactive", "disabled", "false"].includes(value.toLowerCase().trim()));
};

const patientKey = (item: any): string => String(
  item?.dbId ?? item?.db_id ?? item?.value?.id ?? item?.value ?? item?.id ?? ""
);

// Keep existing inactive selections even when react-select emits a removal action.
const preserveInactivePatients = (
  previous: PatientOption[], next: readonly PatientOption[]
): PatientOption[] => {
  const nextById = new Map(next.map(item => [patientKey(item), item]));
  const protectedOptions = previous.filter(item => isInactivePatient(item));
  const merged = Array.from(nextById.values()).map(item => {
    const existing = previous.find(old => patientKey(old) === patientKey(item));
    return existing && isInactivePatient(existing) ? existing : item;
  });
  for (const item of protectedOptions) {
    if (!nextById.has(patientKey(item))) merged.push(item);
  }
  return merged;
};

const MIN_SEARCH_LENGTH = 3;
const DEFAULT_QA_PREFIX = "vip-patient-selector";

const qaSafe = (value: string | number | undefined | null) =>
  String(value ?? "unknown")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const buildQaId = (prefix: string, suffix: string | number) =>
  `${qaSafe(prefix)}-${qaSafe(suffix)}`;

const qaAttrs = (prefix: string, suffix: string | number) => {
  const id = buildQaId(prefix, suffix);

  return {
    id,
    "data-testid": id,
    "data-qa": id,
  };
};

const formatPatients = (data: any[]): PatientOption[] => {
  const map = new Map<string, PatientOption>();

  data.forEach((patient: any) => {
    // IMPORTANT:
    // patient.id = SQL database ID used by validate-all and getPatient/{id}
    const dbId =
      patient?.id ??
      patient?.patient_id ??
      patient?.patientId;

    const fullName = String(
      patient?.Full_name ??
        patient?.full_name ??
        [
          patient?.First_name ?? patient?.first_name,
          patient?.Last_name ?? patient?.last_name,
        ]
          .filter(Boolean)
          .join(" ") ??
        ""
    ).trim();

    const nationalId = String(
      patient?.National_id ??
        patient?.national_id ??
        ""
    ).trim();

    const memberId = String(
      patient?.Member_id ??
        patient?.member_id ??
        ""
    ).trim();

    const patientPassport = String(
      patient?.Patient_passport ??
        patient?.patient_passport ??
        ""
    ).trim();

    // Active-only results are required even if the API ignores the active filter.
    if (isInactivePatient(patient)) return;

    if (dbId === null || dbId === undefined || !fullName) {
      console.warn(
        "Patient skipped because database id or Full_name is missing:",
        patient
      );
      return;
    }

    const key = String(dbId);

    if (!map.has(key)) {
      const identifiers = [
        nationalId ? `National ID: ${nationalId}` : "",
        memberId ? `Member ID: ${memberId}` : "",
        patientPassport ? `Passport: ${patientPassport}` : "",
      ].filter(Boolean);

      map.set(key, {
        // SQL database ID — Claim Validation sends this to validate-all
        // and uses it for GET /Patient/getPatient/{id}.
        value: String(dbId),
        label:
          identifiers.length > 0
            ? `${fullName} (${identifiers.join(" | ")})`
            : fullName,
        patientName: fullName,
        nationalId,
        memberId,
        patientPassport,
        dbId: Number(dbId),
        is_active: patient?.is_active ?? patient?.Is_active ?? patient?.active,
        status: patient?.status ?? patient?.Status,
      } as PatientOption);
    }
  });

  return Array.from(map.values());
};


const normalizeSelectedPatients = (
  incoming: Option[] | undefined,
  allPatients: PatientOption[]
): PatientOption[] => {
  if (!Array.isArray(incoming)) return [];

  return incoming.map((raw: any) => {
    const rawValue = String(
      raw?.dbId ??
      raw?.db_id ??
      raw?.value ??
      raw?.id ??
      ""
    ).trim();

    const rawName = String(
      raw?.patientName ??
      raw?.full_name ??
      raw?.Full_name ??
      raw?.name ??
      raw?.label ??
      ""
    )
      .replace(/^\(\d+\)\s*/, "")
      .replace(/\s*\((?:National ID|Member ID|Passport):.*\)\s*$/, "")
      .trim();

    const rawNationalId = String(
      raw?.nationalId ??
      raw?.National_id ??
      raw?.national_id ??
      ""
    ).trim();

    const rawMemberId = String(
      raw?.memberId ??
      raw?.Member_id ??
      raw?.member_id ??
      ""
    ).trim();

    const rawPassport = String(
      raw?.patientPassport ??
      raw?.Patient_passport ??
      raw?.patient_passport ??
      raw?.passport ??
      ""
    ).trim();

    const canonical =
      allPatients.find((patient) => String(patient.dbId ?? (patient as any).value) === rawValue) ??
      allPatients.find(
        (patient) =>
          rawNationalId &&
          String(patient.nationalId ?? "") === rawNationalId
      ) ??
      allPatients.find(
        (patient) =>
          rawMemberId &&
          String(patient.memberId ?? "") === rawMemberId
      ) ??
      allPatients.find(
        (patient) =>
          rawPassport &&
          String(patient.patientPassport ?? "") === rawPassport
      ) ??
      allPatients.find(
        (patient) =>
          rawName &&
          String(patient.patientName ?? "").trim().toLowerCase() ===
            rawName.toLowerCase()
      );

    // The relation may contain an inactive patient excluded from the active-only list.
    // Its original status must win over the active catalogue when editing.
    if (isInactivePatient(raw)) {
      return {
        ...(canonical ?? {}), ...raw,
        value: rawValue || String(canonical?.value ?? ""),
        label: String(raw?.label ?? canonical?.label ?? rawName),
        __inactive: true,
      } as PatientOption;
    }
    if (canonical) return canonical;
    return { ...raw, __inactive: isInactivePatient(raw) } as PatientOption;
  });
};

const fetchAllPatients = async (): Promise<PatientOption[]> => {
  // Backend Swagger:
  // GET /Patient/getPatient
  return PatientRelationAxiosInstance.get("getPatient", {
    params: {
      page: 1,
      limit: 1000,
      active: "true",
      is_deleted: "false",
    },
  })
    .then((response) => {
      const rawResult =
        response.data?.result ??
        response.data?.patients ??
        response.data?.data ??
        [];

      const data = Array.isArray(rawResult)
        ? rawResult
        : Array.isArray(rawResult?.data)
          ? rawResult.data
          : [];

      const formatted = formatPatients(data);
      return formatted;
    });
};

const VipPatientSelector = ({
  onChange,
  clear,
  defaultValue,
  value,
  qaIdPrefix = DEFAULT_QA_PREFIX,
}: VipPatientSelectorProps) => {
  const [options, setOptions] = useState<PatientOption[]>([]);
  const [allOptions, setAllOptions] = useState<PatientOption[]>([]);
  const [selectedOptions, setSelectedOptions] = useState<PatientOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState("");

  const selectQaId = buildQaId(qaIdPrefix, "select");
  const inputQaId = buildQaId(qaIdPrefix, "input");

  // -----------------------------------------------------------------
  // 1. Load patients once using the current backend endpoint
  // -----------------------------------------------------------------
  const loadAllPatients = useCallback(async () => {
    setLoading(true);

    try {
      const formatted = await fetchAllPatients();
      setAllOptions(formatted);
      setOptions(formatted);
    } catch (err) {
      console.error("loadAllPatients error:", err);
      setAllOptions([]);
      setOptions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadAllPatients();
  }, [loadAllPatients]);

  // -----------------------------------------------------------------
  // 2. Local search only — no /searchpatient request while typing
  // -----------------------------------------------------------------
  useEffect(() => {
    const query = inputValue.trim().toLowerCase();

    if (query.length === 0) {
      setOptions(allOptions);
      return;
    }

    if (query.length < MIN_SEARCH_LENGTH) {
      setOptions([]);
      return;
    }

    const filtered = allOptions.filter((option) => {
      const patient = option as PatientOption;

      return (
        option.label.toLowerCase().includes(query) ||
        String(option.value).toLowerCase().includes(query) ||
        String(patient.patientName ?? "").toLowerCase().includes(query) ||
        String(patient.nationalId ?? "").toLowerCase().includes(query) ||
        String(patient.memberId ?? "").toLowerCase().includes(query) ||
        String(patient.patientPassport ?? "").toLowerCase().includes(query)
      );
    });

    setOptions(filtered);
  }, [inputValue, allOptions]);

  // -----------------------------------------------------------------
  // 3. Input handler
  // -----------------------------------------------------------------
  const onInputChange = (
    newValue: string,
    actionMeta: InputActionMeta
  ): string => {
    if (actionMeta.action === "input-change") {
      setInputValue(newValue);
    }

    return newValue;
  };

  // -----------------------------------------------------------------
  // 4. Selection handler
  // -----------------------------------------------------------------
  const handleChange = (selected: MultiValue<PatientOption>) => {
    const next = preserveInactivePatients(selectedOptions, selected);
    setSelectedOptions(next);
    onChange(next);
  };

  // -----------------------------------------------------------------
  // 5. Clear / external value sync
  // -----------------------------------------------------------------
  useEffect(() => {
    if (clear) {
      // A Clear All action must never silently remove already-linked inactive patients.
      setSelectedOptions(previous => previous.filter(isInactivePatient));
    }
  }, [clear]);

  useEffect(() => {
    if (value) {
      setSelectedOptions(normalizeSelectedPatients(value, allOptions));
    }
  }, [value, allOptions]);

  useEffect(() => {
    if (!value && defaultValue) {
      setSelectedOptions(
        normalizeSelectedPatients(defaultValue, allOptions)
      );
    }
  }, [defaultValue, value, allOptions]);

  // -----------------------------------------------------------------
  // 6. Custom message when < 3 chars
  // -----------------------------------------------------------------
  const noOptionsMessage = () => {
    if (!inputValue) return "Start typing or select from list...";

    if (inputValue.trim().length < MIN_SEARCH_LENGTH) {
      return `Type at least ${MIN_SEARCH_LENGTH} letters to search...`;
    }

    return "No patients found";
  };

  // -----------------------------------------------------------------
  // 7. Styles
  // -----------------------------------------------------------------
  const customStyles = {
    control: (s: any) => ({
      ...s,
      minHeight: "30px",
      fontSize: "14px",
    }),
    multiValue: (s: any, state: any) => ({
      ...s,
      backgroundColor: isInactivePatient(state.data) ? "#d1d5db" : "#007BFF",
      border: isInactivePatient(state.data) ? "1px solid #94a3b8" : "1px solid transparent",
      borderRadius: "4px",
      padding: "2px 4px",
    }),
    multiValueLabel: (s: any, state: any) => ({
      ...s,
      color: isInactivePatient(state.data) ? "#334155" : "#FFF",
      fontWeight: isInactivePatient(state.data) ? 600 : undefined,
      fontSize: "12px",
    }),
    multiValueRemove: (s: any, state: any) => ({
      ...s,
      display: isInactivePatient(state.data) ? "none" : "flex",
      color: "#FFF",
      ":hover": {
        backgroundColor: "#0056b3",
      },
    }),
    option: (
      s: any,
      { isSelected, isFocused }: any
    ) => ({
      ...s,
      backgroundColor: isSelected
        ? "#007BFF"
        : isFocused
          ? "#E6F0FA"
          : "#FFF",
      color: isSelected ? "#FFF" : "#000",
      fontSize: "14px",
    }),
  };

  const Menu = (props: MenuProps<PatientOption, true>) => (
    <div {...qaAttrs(qaIdPrefix, "menu")}>
      <components.Menu {...props} />
    </div>
  );

  const OptionComponent = (
    props: OptionProps<PatientOption, true>
  ) => (
    <div
      {...qaAttrs(
        qaIdPrefix,
        `option-${qaSafe(String(props.data.value))}`
      )}
      data-option-label={props.data.label}
      data-option-value={props.data.value}
      data-patient-db-id={props.data.dbId ?? props.data.value}
      data-patient-name={props.data.patientName ?? ""}
      data-national-id={props.data.nationalId ?? ""}
      data-member-id={props.data.memberId ?? ""}
      data-passport={props.data.patientPassport ?? ""}
    >
      <components.Option {...props} />
    </div>
  );

  const MultiValueComponent = (
    props: MultiValueProps<PatientOption, true>
  ) => (
    <div
      {...qaAttrs(
        qaIdPrefix,
        `selected-${qaSafe(String(props.data.value))}`
      )}
      data-selected-label={props.data.label}
      data-selected-value={props.data.value}
      data-patient-db-id={props.data.dbId ?? props.data.value}
      data-patient-name={props.data.patientName ?? ""}
      data-national-id={props.data.nationalId ?? ""}
      data-member-id={props.data.memberId ?? ""}
      data-passport={props.data.patientPassport ?? ""}
    >
      <components.MultiValue {...props} />
    </div>
  );

  const MultiValueRemove = (props: MultiValueRemoveProps<PatientOption, true>) =>
    isInactivePatient(props.data) ? null : <components.MultiValueRemove {...props} />;

  return (
    <div {...qaAttrs(qaIdPrefix, "wrapper")}>
      <Select
        isMulti
        isClearable={!selectedOptions.some(isInactivePatient)}
        closeMenuOnSelect={false}
        inputId={inputQaId}
        instanceId={selectQaId}
        name={selectQaId}
        classNamePrefix={selectQaId}
        className={selectQaId}
        aria-label="Patient selector"
        data-testid={selectQaId}
        data-qa={selectQaId}
        value={selectedOptions}
        onChange={handleChange}
        onInputChange={onInputChange}
        inputValue={inputValue}
        options={options.filter(option => !isInactivePatient(option))}
        isLoading={loading}
        placeholder="Select patient..."
        styles={customStyles}
        noOptionsMessage={noOptionsMessage}
        components={{
          Menu,
          Option: OptionComponent,
          MultiValue: MultiValueComponent,
          MultiValueRemove,
        }}
      />

      <span
        {...qaAttrs(qaIdPrefix, "loading-state")}
        data-loading={loading ? "true" : "false"}
        style={{ display: "none" }}
      >
        {loading ? "Loading" : "Idle"}
      </span>

      <span
        {...qaAttrs(qaIdPrefix, "selected-count")}
        data-count={selectedOptions.length}
        style={{ display: "none" }}
      >
        {selectedOptions.length}
      </span>

      <span
        {...qaAttrs(qaIdPrefix, "options-count")}
        data-count={options.length}
        style={{ display: "none" }}
      >
        {options.length}
      </span>

      <span
        {...qaAttrs(qaIdPrefix, "input-value")}
        data-value={inputValue}
        style={{ display: "none" }}
      >
        {inputValue}
      </span>
    </div>
  );
};

export default VipPatientSelector;
